import type { Dispatch, SetStateAction } from "react"
import {
  createContext,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"

import { Box, ButtonBase, Collapse, Fade, Stack, Typography, useMediaQuery } from "@mui/material"
import { alpha, useTheme } from "@mui/material/styles"
import type { ResponsiveStyleValue } from "@mui/system"
import { find, get, noop, set } from "lodash"
import PerfectScrollbar from "react-perfect-scrollbar"

import { useScrollDirection } from "@ggs/hooks"
import type { FC, FCWC, ResponsiveImageType } from ".."
import { Button, ContainerStack, Image, Link, ResponsiveImage, SvgIcon } from ".."
import { LinkType } from "../../graphql/types"

import "react-perfect-scrollbar/dist/css/styles.css"
import "./Header.css"

export type HeaderNavLink = {
  title: string
  link: LinkType
  description?: string
  image?: ResponsiveImageType
  children?: HeaderNavLink[]
}

// A context to store data about the currently open menu item and the currently hovered menu item
const HeaderNavContext = createContext<{
  open: [openId: string, setOpenId: Dispatch<SetStateAction<string>>]
  hover: [hoverId: string, setHoverId: Dispatch<SetStateAction<string>>]
  numItems: number
}>({ open: ["", noop], hover: ["", noop], numItems: 0 })

// A id counter for the nav items
let navItemId = 0

/** A sub-component for the main nav section of the header. */
export const HeaderNav: FC<{ links: HeaderNavLink[] }> = ({ links }) => {
  navItemId = 0 // reset the navItemId counter
  const [openId, setOpenId] = useState("")
  const [hoverId, setHoverId] = useState("")
  const direction = useScrollDirection()
  const menuRef = useRef<HTMLDivElement>()
  useEffect(() => {
    if (direction === "DOWN") {
      setOpenId("")
      setHoverId("")
    }
  }, [direction])

  useEffect(() => {
    const handler = (event: Event | React.SyntheticEvent) => {
      // @ts-ignore
      if (!menuRef?.current?.contains(event.target)) {
        setOpenId("")
        setHoverId("")
      }
    }

    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [openId])

  return (
    <HeaderNavContext.Provider
      value={{ open: [openId, setOpenId], hover: [hoverId, setHoverId], numItems: links.length }}
    >
      <Box
        ref={menuRef}
        component="nav"
        data-name="header-main-nav"
        borderTop={(theme) => `1px solid  ${alpha(theme.palette["neutral.900"].main, 0.08)}`}
        display={{ xs: "none", md: "block" }}
      >
        <ContainerStack
          direction="row"
          alignItems="stretch"
          justifyContent={{ xs: "space-around", lg: "space-evenly" }}
        >
          {links.map((item, i) => (
            <HeaderNavItem key={i} item={item} />
          ))}
        </ContainerStack>
      </Box>
    </HeaderNavContext.Provider>
  )
}

// A context to store the unique id of this menu item
const HeaderNavItemIdContext = createContext("")

/** A sub-component for the individual nav item in the main nav of the header. */
const HeaderNavItem: FC<{ item: HeaderNavLink }> = ({ item }) => {
  // The unique id of this menu item
  const { current: uniqueId } = useRef(`${navItemId++}`)
  const { children = [], title } = item

  const attributes = get(item, "link.options.attributes.class", [] as string[])
  const hasChildren = !!children && children.length > 0
  const hasTabs = attributes.some((item) => item.includes("tabs"))

  return (
    <HeaderNavItemIdContext.Provider value={uniqueId}>
      {/* If nav item has children, it's a dropdown so render a trigger with content, else render a link */}
      {hasChildren ? (
        <>
          <HeaderNavItemTrigger label={title} />
          <HeaderNavItemDropdown wide={attributes.some((item) => item.includes("wide"))}>
            {hasTabs ? (
              <HeaderNavItemTabsContent items={children as HeaderNavLink[]} />
            ) : (
              <HeaderNavItemListContent items={children as HeaderNavLink[]} title={item?.title} />
            )}
          </HeaderNavItemDropdown>
        </>
      ) : (
        <HeaderNavItemTopLink item={item} />
      )}
    </HeaderNavItemIdContext.Provider>
  )
}

/** A sub-component for a link in the main nav of the header. */
const HeaderNavItemTopLink: FC<{ item: HeaderNavLink }> = ({ item }) => {
  const uniqueId = useContext(HeaderNavItemIdContext)
  const {
    open: [openId],
    hover: [hoverId, setHoverId],
    numItems,
  } = useContext(HeaderNavContext)

  const isActive = openId === uniqueId

  return (
    <Link
      type="base"
      link={{ ...item.link, title: item.title }}
      data-name="header-nav-item-trigger"
      href={item.link.uri}
      onMouseEnter={() => setHoverId(uniqueId)}
      onMouseLeave={() => setHoverId(openId ? openId : "")}
      onFocus={() => setHoverId(uniqueId)}
      onBlur={() => setHoverId(openId ? openId : "")}
      // accessibility inputs for keyboard navigation
      onKeyDown={(e) => {
        if (!["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight"].includes(e.key)) return

        const triggers = document.querySelectorAll<HTMLButtonElement | HTMLAnchorElement>(
          "[data-name='header-nav-item-trigger']"
        )
        if (e.key === "ArrowRight" || e.key === "ArrowDown") {
          const nextIndex = (parseInt(uniqueId) + 1) % numItems
          triggers[nextIndex].focus()
        } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
          const nextIndex = (((parseInt(uniqueId) - 1) % numItems) + numItems) % numItems
          triggers[nextIndex].focus()
        }
      }}
      sx={(theme) => ({
        ...theme.typography["link.sm"],
        "color": theme.palette["neutral.700"].main,
        "cursor": "pointer",
        "transition": (theme) => theme.transitions.create("opacity"),
        ...(hoverId !== "" && { opacity: 0.7 }),
        ...(isActive && { opacity: 1 }),

        "&:hover, &:focus-visible": {
          opacity: 1,
          outline: 0,
        },
      })}
    >
      <Box p={4} display="inline-block">
        {item.title}
      </Box>
    </Link>
  )
}

/** A sub-component for a dropdown trigger in the main nav of the header. */
const HeaderNavItemTrigger: FCWC<{ label: string }> = ({ label }) => {
  const ref = useRef<HTMLButtonElement>(null)
  const uniqueId = useContext(HeaderNavItemIdContext)
  const {
    open: [openId, setOpenId],
    hover: [hoverId, setHoverId],
    numItems,
  } = useContext(HeaderNavContext)

  const isActive = openId === uniqueId

  return (
    <ButtonBase
      ref={ref}
      data-name="header-nav-item-trigger"
      // configure id context for hover, focus, and click states
      onMouseEnter={() => {
        setHoverId(uniqueId)
      }}
      onMouseLeave={() => {
        setHoverId(openId ? openId : "")
      }}
      onFocus={() => setHoverId(uniqueId)}
      onBlur={() => setHoverId(openId ? openId : "")}
      onClick={() => setOpenId((id) => (id !== uniqueId ? uniqueId : ""))}
      // accessibility inputs for keyboard navigation
      onKeyDown={(e) => {
        if (!["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight"].includes(e.key)) return

        const triggers = document.querySelectorAll<HTMLButtonElement | HTMLAnchorElement>(
          "[data-name='header-nav-item-trigger']"
        )
        if (e.key === "ArrowRight" || e.key === "ArrowDown") {
          const nextIndex = (parseInt(uniqueId) + 1) % numItems
          triggers[nextIndex].focus()
        } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
          const nextIndex = (((parseInt(uniqueId) - 1) % numItems) + numItems) % numItems
          triggers[nextIndex].focus()
        }
      }}
      sx={(theme) => ({
        ...theme.typography["link.sm"],
        "color": theme.palette["neutral.700"].main,
        "cursor": "pointer",
        "transition": (theme) => theme.transitions.create("opacity"),
        ...(hoverId !== "" && { opacity: 0.7 }),
        ...(isActive && { opacity: 1 }),

        "&:hover, &:focus-visible": {
          opacity: 1,
        },
      })}
    >
      <Stack
        display="inline-flex"
        direction="row"
        alignItems="center"
        columnGap={1.5}
        py={4}
        height="100%"
      >
        {label}
        <SvgIcon
          variant="bullet"
          size={8}
          sx={{
            transition: "transform 0.3s ease",
            transform: `rotate(${isActive ? "-90deg" : "90deg"})`,
          }}
        />
      </Stack>
    </ButtonBase>
  )
}

/** A sub-component for the dropdown content in the main nav of the header. */
const HeaderNavItemDropdown: FCWC<{ wide?: boolean }> = ({ wide = false, children }) => {
  const uniqueId = useContext(HeaderNavItemIdContext)
  const {
    open: [openId],
  } = useContext(HeaderNavContext)
  const direction = useScrollDirection()

  const isOpen = openId === uniqueId

  return (
    <Box
      position="absolute"
      top="100%"
      left="50%"
      width={`min(${wide ? 1560 : 1220}px, 100%)`}
      sx={{
        transform: "translateX(-50%)",
      }}
    >
      <Collapse in={isOpen} timeout={200}>
        <Box px={5} pb={5} overflow="hidden">
          <Box
            bgcolor={(theme) => theme.palette["neutral.100"].main}
            borderRadius="0 0 4px 4px"
            maxHeight="70vh"
            overflow="auto"
            sx={{
              boxShadow: (theme) =>
                direction === "DOWN"
                  ? "none"
                  : `0px 0px 10px 0px ${alpha(theme.palette["neutral.900"].main, 0.2)}`,
            }}
          >
            {children}
          </Box>
        </Box>
      </Collapse>
    </Box>
  )
}

// A context to store the open index of the tabs
const HeaderNavItemTabsContext = createContext<{
  state: [openIndex: number, setOpenIndex: Dispatch<SetStateAction<number>>]
  numItems: number
}>({ state: [0, noop], numItems: 0 })

/** A sub-component for the tabs in the dropdown content in the main nav of the header. */
const HeaderNavItemTabsContent: FC<{ items: HeaderNavLink[] }> = ({ items }) => {
  const contextValues = useState(0)
  return (
    <HeaderNavItemTabsContext.Provider value={{ state: contextValues, numItems: items.length }}>
      <Box
        display="grid"
        gridTemplateColumns={"clamp(256px, 23.42%, 356px) auto"}
        overflow="hidden"
      >
        <HeaderNavItemTabBtns>
          {items.length > 6 && (
            <Typography
              variant="body.sm"
              component="div"
              sx={{ marginBottom: "10px !important", marginTop: "14px !important" }}
              pl={4}
            >
              Explore by Region
            </Typography>
          )}

          {items.map(
            (item, i) =>
              item.title !== "All Destinations" && (
                <HeaderNavItemTabBtn key={i} index={i} label={item.title} />
              )
          )}
          {items.length > 6 && (
            <Button
              href={"/destinations"}
              variant="tertiary"
              sx={{
                "backgroundColor": "#F05B2A", // Smooth transition for hover effect
                "&:hover": {
                  backgroundColor: "#D04A22",
                },
                "width": "100%",
                "minHeight": "48px !important",
                "marginTop": "2rem !important",
                "textWrap": "nowrap",
              }}
            >
              Explore All Destinations
            </Button>
          )}
        </HeaderNavItemTabBtns>
        <HeaderNavItemTabPanels>
          {items.map((item, i) => (
            <Fragment key={i}>
              {item.link.options?.attributes?.class?.includes("destination-map") ? (
                <HeaderNavItemDestinationTabPanel
                  key={i}
                  index={i}
                  uri={items[items.length - 1].link.uri}
                />
              ) : (
                <HeaderNavItemTabPanel key={i} index={i} item={item} />
              )}
            </Fragment>
          ))}

          {/* The last item should be the "All Destinations" tab which has a special panel */}
        </HeaderNavItemTabPanels>
      </Box>
    </HeaderNavItemTabsContext.Provider>
  )
}

/** A sub-component for the tab buttons in the dropdown content in the main nav of the header. */
const HeaderNavItemTabBtns: FCWC = ({ children }) => {
  return (
    <Box component={PerfectScrollbar} maxHeight="70vh" flex="1 1 356px">
      <Stack role="tablist" maxHeight="70vh" spacing={0.5} px={7} py={6}>
        {children}
      </Stack>
    </Box>
  )
}

/** A sub-component for the individual tab button in the dropdown content in the main nav of the header. */
const HeaderNavItemTabBtn: FC<{ index: number; label: string }> = ({ index, label }) => {
  const ref = useRef<HTMLButtonElement>(null)
  const {
    state: [openIndex, setOpenIndex],
    numItems,
  } = useContext(HeaderNavItemTabsContext)
  const isActive = openIndex === index

  const getElementSiblings = useCallback((e: HTMLButtonElement) => {
    const siblings: HTMLButtonElement[] = []

    if (!e.parentNode) return siblings
    let sibling = e.parentNode.firstChild as HTMLButtonElement
    while (sibling) {
      if (sibling.nodeType === 1) {
        siblings.push(sibling)
      }
      sibling = sibling.nextSibling as HTMLButtonElement
    }

    return siblings
  }, [])

  return (
    <ButtonBase
      ref={ref}
      role="tab"
      aria-selected={isActive}
      aria-controls={`header-nav-menu-tabpanel-${index}`}
      tabIndex={isActive ? 0 : -1}
      onMouseEnter={() => setOpenIndex(index)}
      onClick={() => setOpenIndex(index)}
      onFocus={() => setOpenIndex(index)}
      onKeyDown={(e) => {
        if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return

        const buttons = getElementSiblings(ref.current!)
        if (e.key === "ArrowDown") {
          const nextIndex = (openIndex + 1) % numItems
          buttons[nextIndex].focus()
        } else if (e.key === "ArrowUp") {
          const nextIndex = (((openIndex - 1) % numItems) + numItems) % numItems
          buttons[nextIndex].focus()
        }
      }}
      sx={(theme) => ({
        ...theme.typography["link.sm"],
        "lineHeight": 1.2,

        "bgcolor": isActive ? theme.palette["secondary.200"].main : "transparent",
        "color": isActive ? theme.palette["secondary.800"].main : theme.palette["neutral.700"].main,
        "borderRadius": 0.8,
        "display": "flex",
        "height": 52,
        "justifyContent": "space-between",
        "px": 4,
        "py": 2.5,
        "textAlign": "left",
        "width": "100%",

        "&:hover, &:focus": !isActive && {
          bgcolor: theme.palette["secondary.200"].main,
        },
      })}
    >
      {label}
      {isActive && <SvgIcon variant="bullet" size={8} />}
    </ButtonBase>
  )
}

/** A sub-component for the tab panels in the dropdown content in the main nav of the header. */
const HeaderNavItemTabPanels: FCWC = ({ children }) => {
  return (
    <Box
      maxHeight="70vh"
      flex="1 1 100%"
      position="relative"
      sx={{
        borderLeft: (theme) => `1px solid ${theme.palette["neutral.200"].main}`,
      }}
    >
      {children}
    </Box>
  )
}

/** A sub-component for the individual tab panel in the dropdown content in the main n345av of the header. */
const HeaderNavItemTabPanel: FC<{ index: number; item: HeaderNavLink }> = ({ index, item }) => {
  const {
    state: [openIndex],
  } = useContext(HeaderNavItemTabsContext)
  const open = openIndex === index
  const { children = [] as HeaderNavLink[] } = item

  const links =
    children.length < 20
      ? children.filter((child) => !child.image || child.image.variants.length === 0)
      : children
  const images = children
    .filter((child) => child.image && child.image.variants.length > 0)
    .slice(0, 2)

  const theme = useTheme()
  const isPopularDestination = item.title === "Popular Destinations"
  const isXlBreakpoint = useMediaQuery(theme.breakpoints.up("xl"))

  const columnCount = (x: number) => {
    if (x <= 5) return 1
    if (x <= 16) return 2
    if (x <= 30) return 3
    return 4
  }

  const isCountry = links[1].link.uri.startsWith("/destination")
  return (
    <Fade in={open}>
      <Box
        role="tabpanel"
        aria-label={`Explore ${item.title}`}
        height="100%"
        display={open ? "block" : "none"}
      >
        <Stack direction={{ xl: "row" }} height="100%" position="relative">
          <Box
            component={PerfectScrollbar}
            flex="1 1 100%"
            p={isCountry ? 6 : 17.5}
            pb={{ md: 17.5, xl: 11.5 }}
            pl={isCountry ? 7 : 17.5}
            pr={{ md: 17.5, xl: 7.5 }}
            display="flex"
            flexDirection="column"
          >
            {/* Insure titles are updated depending on active nav tab view */}
            {!isCountry ? (
              <Typography variant="body.sm" component="div" mb={9.5}>
                Explore
              </Typography>
            ) : (
              <Typography variant="body.sm" component="div" mb={5} mt={3.5}>
                {isPopularDestination ? "Explore" : "Popular Countries"}
              </Typography>
            )}
            <HeaderNavItemLinkLayout
              items={links}
              columns={{
                md: Math.min(columnCount(links.length), 2),
                lg: Math.min(columnCount(links.length), 3),
                xl: Math.min(columnCount(links.length), 4),
              }}
            />
            <Box mt="auto" pt={10}>
              {isCountry && !isPopularDestination && (
                <Link href={item.link.uri}>Explore all {item.title}</Link>
              )}
            </Box>
          </Box>
          {images.length > 0 && <HeaderNavItemImages images={images} isDestination={isCountry} />}
        </Stack>
      </Box>
    </Fade>
  )
}

const HeaderNavItemDestinationTabPanel: FC<{ index: number; uri: string }> = ({ index, uri }) => {
  const {
    state: [openIndex],
  } = useContext(HeaderNavItemTabsContext)
  const open = openIndex === index

  return (
    <Fade in={open}>
      <Box
        role="tabpanel"
        aria-labelledby={`header-nav-menu-tabpanel-${index}`}
        height="100%"
        display={open ? "block" : "none"}
      >
        <Stack
          alignItems="center"
          justifyContent="center"
          height="100%"
          position="relative"
          overflow="hidden"
        >
          <DestinationTabPanelIllustration />
          <Button href={uri} variant="tertiary">
            Explore All Destinations
          </Button>
        </Stack>
      </Box>
    </Fade>
  )
}

/** A sub-component for the list content in the dropdown content in the main nav of the header. */
const HeaderNavItemListContent: FC<{ items: HeaderNavLink[]; title: string }> = ({
  items,
  title,
}) => {
  const links = items.filter((item) => !item.image || item.image.variants.length === 0)
  const images = items.filter((item) => item.image && item.image.variants.length > 0).slice(0, 2)
  return (
    <Stack direction={{ xl: "row" }} height="100%" position="relative">
      {/* <HeaderNavTabListPanel links={links} /> */}

      <Box component={PerfectScrollbar} flex="1 1 100%" p={17.5}>
        {title && (
          <Typography variant="body.sm" mb={3.5}>
            {title}
          </Typography>
        )}
        <HeaderNavItemListLinks items={links} />
      </Box>
      {images.length > 0 && <HeaderNavItemImages images={images} />}
    </Stack>
  )
}

/** A sub-component for the list of links in the dropdown content in the main nav of the header. */
const HeaderNavItemListLinks: FC<{ items: HeaderNavLink[] }> = ({ items }) => {
  const noCategory = items.some((item) => {
    const attributes = get(item, "link.options.attributes.class", [] as string[])
    return !attributes.some((item) => item.includes("category-title"))
  })

  if (noCategory)
    return (
      <Stack justifyContent="center" pt={6}>
        <HeaderNavItemLinkLayout items={items} columns={Math.min(Math.ceil(items.length / 4), 4)} />
      </Stack>
    )

  return (
    <Stack direction="row" columnGap={10}>
      {items.map((item, i) => {
        const children = get(item, "children", [] as HeaderNavLink[]) ?? []
        return (
          <Box key={i} flex="auto">
            <Typography variant="body.sm" component="div" mb={10}>
              {item.title}
            </Typography>

            <HeaderNavItemLinkLayout items={children} columns={Math.ceil(children.length / 4)} />
          </Box>
        )
      })}
    </Stack>
  )
}

let linkId = 0
// A context to store the unique id of this link for hover state
const HeaderNavLinkHoverContext = createContext<
  [hoverId: string, setHoverId: Dispatch<SetStateAction<string>>]
>(["", noop])

/** A sub-component for the link layout in the dropdown content in the main nav of the header. */
const HeaderNavItemLinkLayout: FC<{
  items: HeaderNavLink[]
  columns: ResponsiveStyleValue<number>
}> = ({ items, columns }) => {
  linkId = 0

  return (
    <HeaderNavLinkHoverContext.Provider value={useState("")}>
      <Box sx={{ columnCount: 3, columnGap: 10 }}>
        {items.map(
          (item, i) =>
            item.title !== "Popular Destination" && (
              <Typography
                component="div"
                variant="link.sm"
                key={i}
                mb={5.5}
                sx={{ lineHeight: 1.4 }}
              >
                <HeaderNavItemLink item={item} />
              </Typography>
            )
        )}
      </Box>
      {/* Conditionally rendering Popular Destinations coming in from Drupal */}
      {items.map(
        (item, i) =>
          item.title === "Popular Destination" && (
            <>
              <Typography variant="body.sm" component="div" mt={9.5} mb={5}>
                Popular Destinations
              </Typography>
              <Box sx={{ columnCount: 3, columnGap: 10 }}>
                {item.children?.map((item, i) => (
                  <Typography
                    component="div"
                    variant="link.sm"
                    key={i}
                    mb={5.5}
                    sx={{ lineHeight: 1.4 }}
                  >
                    <HeaderNavItemLink item={item} />
                  </Typography>
                ))}
              </Box>
            </>
          )
      )}
    </HeaderNavLinkHoverContext.Provider>
  )
}

/** A sub-component for an individual link in dropdown content in the main nav of the header. */
const HeaderNavItemLink: FC<{ item: HeaderNavLink }> = ({ item }) => {
  const [hoverId, setHoverId] = useContext(HeaderNavLinkHoverContext)
  const { current: uniqueId } = useRef(`${linkId++}`)
  return (
    <Link
      type="base"
      link={{ ...item.link, title: item.title }}
      href={item.link.uri}
      onMouseEnter={() => setHoverId(uniqueId)}
      onMouseLeave={() => setHoverId("")}
      onFocus={() => setHoverId(uniqueId)}
      onBlur={() => setHoverId("")}
      variant="link.sm"
      underline="none"
      sx={(theme) => ({
        "color": theme.palette["neutral.700"].main,
        "transition": theme.transitions.create(["color", "opacity", "font-size"]),
        "breakInside": "avoid",

        ...(hoverId !== "" && { opacity: 0.7 }),

        "&:hover, &:focus-visible": {
          opacity: 1,
          outline: "none",
        },

        [theme.breakpoints.between(768, 1300)]: {
          fontSize: 12,
        },
      })}
    >
      {item.title}
    </Link>
  )
}

/** A sub-component for an individual image in the dropdown content in the main nav of the header. */
const HeaderNavItemImages: FC<{ images: HeaderNavLink[]; isDestination?: Boolean }> = ({
  images,
  isDestination,
}) => (
  <Box
    p={6}
    //pt={{ xs: 0, xl: 6 }}
    flex={{ xs: "1 1 270px", xl: "1 0 400px" }}
    display="grid"
    gridAutoFlow={{ xs: "column", xl: "row" }}
    rowGap={5}
    columnGap={5}
  >
    {images.map((item, i) => {
      const portrait = find(item.image?.variants, ["style", "portrait"])
      const landscape = find(item.image?.variants, ["style", "landscape"])
      const thumbnail = find(item.image?.variants, ["style", "thumbnail"])
      const image: ResponsiveImageType = {
        ...item.image!,
        variants: [],
      }
      if (images.length === 1) {
        image.variants.push({ ...portrait!, style: "2xl" })
      }

      image.variants.push({ ...landscape!, style: "lg" })
      image.variants.push({ ...thumbnail!, style: "default" })
      return (
        <Box key={i} borderRadius={1.5} overflow="hidden">
          <Box
            position="relative"
            width="100%"
            height="100%"
            sx={{
              "minHeight": 190,
              "isolation": "isolate",
              "&:hover, &:focus-within": {
                img: {
                  transform: "scale(1.05)",
                },
              },
            }}
          >
            <Link
              type="overlay"
              link={{ ...item.link, title: item.title }}
              href={item.link.uri}
              title={item.title}
              zIndex={3}
            />
            <Box
              position="absolute"
              width="100%"
              height="100%"
              sx={{
                background: (theme) =>
                  `linear-gradient(180deg, ${alpha(
                    theme.palette["neutral.900"].main,
                    0
                  )} 0%, ${alpha(theme.palette["neutral.900"].main, 0.4)} 100%)`,
                pointerEvents: "none",
              }}
              zIndex={1}
            />
            <ResponsiveImage
              // img Style={images.length === 1 ? "portrait" : "landscape"}
              image={image}
              sx={{
                width: "100%",
                height: "100%",
                pointerEvents: "none",
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                objectPosition: "center",
                objectFit: "cover",
                transition: (theme) =>
                  theme.transitions.create("transform", {
                    duration: theme.transitions.duration.standard * 1.5,
                  }),
              }}
            />
            <Box
              position="absolute"
              bottom="0"
              width="100%"
              p={5}
              zIndex={2}
              sx={{ pointerEvents: "none" }}
            >
              {!isDestination && (
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  color={(theme) => theme.palette["neutral.100"].main}
                >
                  <Box>
                    {item.description && (
                      <Typography variant="body.xs" component="div">
                        {item.description}
                      </Typography>
                    )}

                    <Typography variant="body.lg" component="div">
                      {item.title}
                    </Typography>
                  </Box>
                  <Box alignSelf="flex-end">
                    <SvgIcon variant="arrowOutward" size={14} />
                  </Box>
                </Stack>
              )}
            </Box>
          </Box>
        </Box>
      )
    })}
  </Box>
)

const DestinationTabPanelIllustration: FC = () => (
  <Image
    src="/images/header-map.png"
    alt="World Map SVG"
    sx={(theme) => ({
      fill: theme.palette["secondary.300"].main,
      position: "absolute",
      left: "50%",
      top: 0,
      bottom: 0,
      width: "auto",
      height: "100%",
      objectFit: "contain",
      transform: "translateX(-50%)",
    })}
  />
)
